import type { ComponentType } from "react";
import {
  Wallet,
  Camera,
  Receipt,
  Sparkles,
  Flame,
  ShieldCheck,
  UtensilsCrossed,
  Users,
  type LucideProps,
} from "lucide-react";

import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { Spark } from "../ui/decor";
import { FEATURES } from "../../content/site";

const ICONS: Record<string, ComponentType<LucideProps>> = {
  wallet: Wallet,
  camera: Camera,
  receipt: Receipt,
  sparkles: Sparkles,
  flame: Flame,
  shield: ShieldCheck,
  utensils: UtensilsCrossed,
  users: Users,
};

// Flat pastel fills cycling across the bento tiles — never the same twice in a row.
const TILE_BG = [
  "bg-teal-mist",
  "bg-sun-pale",
  "bg-violet-mist",
  "bg-coral-pale",
  "bg-cream-deep",
  "bg-white",
];

export function FeaturesGrid() {
  return (
    <section id="features" className="relative py-20 sm:py-28 bg-cream">
      <div className="container-x">
        <SectionHeading
          eyebrow="Inside the app"
          title={
            <>
              Built around one{" "}
              <span className="marker">rewarding second</span>
            </>
          }
          sub="Your balance jumping after a weigh-in is the heart of it. Everything else — AI food logging, meal ideas, friends and streaks — exists to keep you coming back for it."
        />

        <div className="relative mt-14">
          {/* Playful sparks peeking out around the bento. */}
          <Spark className="absolute -top-8 -left-3 h-9 w-9 -rotate-12 sm:-left-8" />
          <Spark
            fill="#CCFBF1"
            className="absolute -bottom-7 -right-2 h-11 w-11 rotate-12 sm:-right-8"
          />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => {
              const Icon = ICONS[feature.icon] ?? Sparkles;
              const hero = i === 0;
              const wide = i === FEATURES.length - 1;
              const tilt = i % 2 === 0 ? "rotate-1" : "-rotate-1";
              return (
                <Reveal
                  key={feature.title}
                  delay={i}
                  className={
                    hero
                      ? "sm:col-span-2 lg:col-span-2"
                      : wide
                        ? "sm:col-span-2 lg:col-span-2"
                        : ""
                  }
                >
                  <div
                    className={`h-full rounded-[1.75rem] border-[3px] border-navy shadow-pop ${TILE_BG[i % TILE_BG.length]} ${tilt} transition-transform duration-200 will-change-transform hover:rotate-0 hover:scale-[1.02] ${
                      hero ? "p-8 sm:p-10" : "p-7"
                    }`}
                  >
                    <div
                      className={`grid place-items-center rounded-2xl border-[3px] border-navy bg-white ${
                        hero ? "h-16 w-16" : "h-14 w-14"
                      }`}
                    >
                      <Icon
                        className={hero ? "h-8 w-8 text-navy" : "h-7 w-7 text-navy"}
                      />
                    </div>
                    <h3
                      className={`display mt-6 text-navy ${
                        hero ? "text-3xl sm:text-4xl" : "text-2xl"
                      }`}
                    >
                      {feature.title}
                    </h3>
                    <p
                      className={`mt-3 leading-relaxed text-charcoal/75 ${
                        hero ? "text-base sm:text-lg max-w-xl" : "text-sm sm:text-base"
                      }`}
                    >
                      {feature.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
